import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Cloud, Shield, Brain, GitMerge, Boxes, Briefcase, Check, Plus, Minus } from "lucide-react";
import SEO from "../components/SEO";
import { SERVICES } from "../data/services";

const ICONS = { cloud: Cloud, shield: Shield, brain: Brain, "git-merge": GitMerge, boxes: Boxes, briefcase: Briefcase };

const FAQItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        borderBottom: "1px solid var(--line)",
      }}
      data-testid={`faq-item-${index}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          width: "100%",
          background: "transparent",
          border: "none",
          padding: "28px 0",
          display: "grid",
          gridTemplateColumns: "40px 1fr 32px",
          gap: 20,
          alignItems: "center",
          cursor: "pointer",
          textAlign: "left",
          color: "var(--ink)",
        }}
      >
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.15em", color: "var(--muted)", fontWeight: 600 }}>
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="display-sm" style={{ fontWeight: 600, fontSize: "clamp(18px, 1.8vw, 22px)", color: open ? "var(--green)" : "var(--ink)", transition: "color 200ms ease" }}>
          {q}
        </span>
        <span
          style={{
            width: 32, height: 32, borderRadius: "50%",
            background: open ? "var(--green)" : "transparent",
            border: open ? "1px solid var(--green)" : "1px solid var(--line-2)",
            color: open ? "#fff" : "var(--ink)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            transition: "all 200ms ease",
            justifySelf: "end",
          }}
        >
          {open ? <Minus size={14} strokeWidth={2.5} /> : <Plus size={14} strokeWidth={2.5} />}
        </span>
      </button>
      <div
        style={{
          maxHeight: open ? 400 : 0,
          overflow: "hidden",
          transition: "max-height 350ms cubic-bezier(.2,.8,.2,1), padding 250ms ease",
          paddingLeft: 60,
          paddingRight: 52,
          paddingBottom: open ? 28 : 0,
        }}
      >
        <p className="body-md" style={{ fontSize: 16, lineHeight: 1.7, maxWidth: 720 }}>{a}</p>
      </div>
    </div>
  );
};

const METRICS = [
  { k: "4 hrs", label: "Deployment cycle — down from 2 weeks" },
  { k: "6→4", label: "ATO timeline, months to weeks" },
  { k: "85%", label: "Reduced lateral threat exposure" },
  { k: "IL5", label: "Ready cloud foundations" },
];

const CLIENT_LOGOS = ["/clients/001.png", "/clients/002.png", "/clients/003.png", "/clients/004.png"];

const CASE_OUTCOMES = [
  { n: "01", t: "From 2 weeks to under 4 hours", d: "Reduced deployment cycle time using automated CI/CD pipelines built around GitLab CI, ArgoCD, and policy-as-code gates." },
  { n: "02", t: "ATO in under 4 weeks", d: "Delivered a fully automated DevSecOps pipeline with integrated STIG compliance, cutting ATO timelines from 6 months to under 4 weeks." },
  { n: "03", t: "IL5-ready, repeatable, audit-grade", d: "Built Infrastructure as Code foundations using Terraform across multi-cloud environments — repeatable, audit-ready deployments for DoD IL5 workloads." },
  { n: "04", t: "85% less lateral exposure", d: "Implemented Zero Trust micro-segmentation and identity-aware access controls, reducing lateral threat exposure across federal application environments." },
];

const INDUSTRIES = [
  { n: "01", t: "Federal Government", d: "Civilian agencies, DoD components, and the intelligence community — FedRAMP, RMF, and DoD Zero Trust by default." },
  { n: "02", t: "Healthcare & Life Sciences", d: "HIPAA, HITRUST, and clinical systems where downtime is measured in patient outcomes." },
  { n: "03", t: "Fortune 500 Enterprise", d: "Regulated industries scaling cloud, security, and platforms across global footprints." },
  { n: "04", t: "Mid-Market & DIB", d: "Growing organizations, CMMC-bound defense contractors, and managed programs." },
];

const Home = () => {
  return (
    <>
      <SEO
        title="Turning Data Into Decisions"
        description="Gravitas Technology Solutions turns data into decisions for federal agencies and Fortune 500 enterprises. DevSecOps, Zero Trust cloud, platform engineering, and continuous compliance from Washington DC."
        path="/"
      />

      {/* ===================== HERO ===================== */}
      <section style={{ paddingTop: 64, paddingBottom: 56, position: "relative", overflow: "hidden" }} data-testid="home-hero">
        <div className="container-grav">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8 anim-fade-up">
              <div className="eyebrow" style={{ marginBottom: 28 }}>
                <span className="sep" />Federal · Enterprise · Mission-Critical
              </div>
              <h1 className="display-xl">
                Turning data<br />
                into <span style={{ color: "var(--green)", fontStyle: "italic", fontWeight: 800 }}>decisions.</span>
              </h1>
            </div>
            <div className="lg:col-span-4 anim-fade-up" style={{ animationDelay: "0.15s" }}>
              <p className="lede" style={{ maxWidth: 380 }}>
                We are the advisory and engineering partner that federal agencies and Fortune 500 operators call when the stakes — ATO deadlines, classified boundaries, 24/7 uptime — leave no room for vendor bias.
              </p>
              <div style={{ marginTop: 32, display: "flex", flexWrap: "wrap", gap: 14 }}>
                <Link to="/services" className="btn" data-testid="hero-cta-explore">
                  Explore what we do <span className="arrow"><ArrowUpRight size={14} strokeWidth={2.5} /></span>
                </Link>
                <Link to="/contact" className="btn btn-ghost" data-testid="hero-cta-consult">
                  Start a brief <span className="arrow"><ArrowUpRight size={14} strokeWidth={2.5} /></span>
                </Link>
              </div>
            </div>
          </div>

          {/* Hero image strip (editorial) */}
          <div style={{ marginTop: 80, position: "relative" }} className="anim-fade-up">
            <img
              src="https://images.pexels.com/photos/586056/pexels-photo-586056.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=700&w=1920"
              alt="Satellite view of earth from space"
              loading="eager"
              style={{ width: "100%", height: "clamp(320px, 42vw, 560px)", objectFit: "cover", display: "block" }}
            />
            {/* floating badge over image */}
            <div className="badge-float" style={{ position: "absolute", left: 32, bottom: -40, background: "var(--bg)", padding: "20px 28px", border: "1.5px solid var(--ink)" }}>
              <div className="eyebrow" style={{ marginBottom: 8 }}>Case outcome</div>
              <div className="display-sm" style={{ maxWidth: 340 }}>
                Deploy cycle: <span className="text-green">2 weeks → under 4 hours.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== METRICS ===================== */}
      <section style={{ padding: "80px 0 40px" }}>
        <div className="container-grav">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }} data-testid="home-metrics">
            {METRICS.map((m, i) => (
              <div key={m.label} style={{ padding: "36px 24px", borderRight: i < METRICS.length - 1 ? "1px solid var(--line)" : "none" }}>
                <div className="font-display" style={{ fontSize: "clamp(44px, 5vw, 64px)", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1 }}>
                  {m.k}
                </div>
                <div style={{ marginTop: 16, color: "var(--muted)", fontSize: 14, lineHeight: 1.5 }}>{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== TRUSTED CLIENTS ===================== */}
      <section style={{ padding: "80px 0", background: "var(--bg-alt)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }} data-testid="trusted-clients">
        <div className="container-grav">
          <div className="eyebrow" style={{ marginBottom: 20 }}><span className="sep" />Meet our trusted clients</div>
          <h2 className="display-md" style={{ maxWidth: 820 }}>
            The teams that keep mission systems running — and the partners they trust to modernize them.
          </h2>
        </div>

        <div style={{ position: "relative", marginTop: 56, width: "100%", overflow: "hidden", maskImage: "linear-gradient(90deg, transparent, black 8%, black 92%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, black 8%, black 92%, transparent)" }}>
          <div className="marquee-track" style={{ display: "flex", gap: 96, width: "max-content", alignItems: "center", padding: "16px 48px" }}>
            {[...CLIENT_LOGOS, ...CLIENT_LOGOS, ...CLIENT_LOGOS, ...CLIENT_LOGOS].map((src, i) => (
              <img
                key={`${src}-${i}`}
                src={src}
                alt="Client"
                loading="lazy"
                style={{ height: 64, width: "auto", objectFit: "contain", opacity: 0.95, flex: "0 0 auto" }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ===================== CASE OUTCOMES ===================== */}
      <section className="section" data-testid="case-outcomes">
        <div className="container-grav">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end" style={{ marginBottom: 64 }}>
            <div className="lg:col-span-8">
              <div className="eyebrow" style={{ marginBottom: 20 }}><span className="sep" />Case outcomes</div>
              <h2 className="display-lg">
                Real results.<br /><span style={{ color: "var(--muted)" }}>Engineered, shipped, measured.</span>
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="lede" style={{ maxWidth: 380 }}>
                A representative slice of recent DevSecOps and cloud engineering work — outcomes our clients can audit.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {CASE_OUTCOMES.map((c) => (
              <div key={c.n} className="tile" style={{ minHeight: 220 }} data-testid={`case-${c.n}`}>
                <div className="accent-bar" />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                  <span className="eyebrow">{c.n}</span>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--green-bg)", color: "var(--green)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                    <Check size={16} strokeWidth={3} />
                  </div>
                </div>
                <h3 className="display-sm" style={{ marginTop: 24 }}>{c.t}</h3>
                <p className="body-md" style={{ marginTop: 12 }}>{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== CAPABILITIES (editorial big rows) ===================== */}
      <section className="section" data-testid="home-capabilities">
        <div className="container-grav">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end" style={{ marginBottom: 72 }}>
            <div className="lg:col-span-8">
              <div className="eyebrow" style={{ marginBottom: 20 }}><span className="sep" />What we do</div>
              <h2 className="display-lg">
                Six practices.<br /><span style={{ color: "var(--muted)" }}>Engineered to ship together.</span>
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="lede" style={{ maxWidth: 380 }}>
                Advisory, engineering, and operations across the disciplines that decide whether a mission system ships — or stalls at ATO review.
              </p>
              <div style={{ marginTop: 28 }}>
                <Link to="/services" className="arrow-link">All capabilities</Link>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon];
              return (
                <Link to={`/services/${s.slug}`} key={s.slug} data-testid={`capability-card-${s.slug}`} className="tile" style={{ display: "block", textDecoration: "none", color: "var(--ink)", minHeight: 300 }}>
                  <div className="accent-bar" />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 40 }}>
                    <span className="eyebrow">{s.code}</span>
                    <div style={{ width: 44, height: 44, borderRadius: "50%", background: "var(--green-bg)", color: "var(--green)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      {Icon && <Icon size={20} strokeWidth={1.75} />}
                    </div>
                  </div>
                  <h3 className="display-sm" style={{ marginBottom: 12 }}>{s.title}</h3>
                  <p className="body-md" style={{ color: "var(--muted)" }}>{s.tagline}</p>
                  <div style={{ marginTop: 32, display: "inline-flex", alignItems: "center", gap: 10, color: "var(--ink)", fontWeight: 600, fontSize: 14 }}>
                    Read more <ArrowUpRight size={16} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== DARK STATEMENT SECTION ===================== */}
      <section style={{ background: "var(--dark)", color: "var(--dark-ink)", padding: "140px 0" }}>
        <div className="container-grav">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="eyebrow" style={{ color: "rgba(246,244,239,0.6)", marginBottom: 28 }}><span className="sep" style={{ background: "rgba(246,244,239,0.6)" }} />Why Gravitas</div>
              <h2 className="display-lg" style={{ color: "var(--dark-ink)" }}>
                The partner you keep on speed-dial <span style={{ color: "var(--green)" }}>when the audit window closes.</span>
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="lede" style={{ color: "rgba(246,244,239,0.75)" }}>
                We sit at the intersection of federal policy, mission-systems engineering, and hyperscale cloud. No handoffs between "strategy" and "delivery" — the engineers who write your ATO package are the ones operating the pipeline at 3 a.m.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0" style={{ marginTop: 96, border: "1px solid rgba(246,244,239,0.15)" }}>
            {[
              { n: "01", t: "DevSecOps DNA", d: "Security isn't an afterthought. We embed it into every pipeline, every deployment, every architecture decision." },
              { n: "02", t: "Deep Federal Compliance Expertise", d: "NIST 800-53, FedRAMP, CMMC 2.0, RMF, DoD Zero Trust — not bolted on, built in." },
              { n: "03", t: "Automation-First Delivery", d: "Infrastructure as Code, Policy as Code, CI/CD everywhere. Faster timelines, lower cost, repeatable results." },
              { n: "04", t: "Proven Mission-Critical Experience", d: "We've operated inside federal SOCs, DoD programs, and Fortune 500 platforms where uptime isn't optional." },
            ].map((p, i) => (
              <div key={p.t} style={{
                padding: "40px 32px",
                borderRight: i < 3 ? "1px solid rgba(246,244,239,0.15)" : "none",
                borderTop: i >= 2 ? "1px solid rgba(246,244,239,0.15)" : "none",
                minHeight: 280,
              }} className="lg:border-t-0">
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.2em", color: "rgba(246,244,239,0.5)", marginBottom: 24, fontWeight: 600 }}>{p.n}</div>
                <h4 className="display-sm" style={{ color: "var(--dark-ink)", marginBottom: 14 }}>{p.t}</h4>
                <p style={{ color: "rgba(246,244,239,0.65)", fontSize: 15, lineHeight: 1.65 }}>{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== INDUSTRIES ===================== */}
      <section className="section">
        <div className="container-grav">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end" style={{ marginBottom: 72 }}>
            <div className="lg:col-span-6">
              <div className="eyebrow" style={{ marginBottom: 20 }}><span className="sep" />Who we serve</div>
              <h2 className="display-lg">Built for the rooms where losing isn't an option.</h2>
            </div>
          </div>

          <div style={{ borderTop: "1px solid var(--ink)" }}>
            {INDUSTRIES.map((g) => (
              <div key={g.t} className="row-editorial row-4" data-testid={`industry-row-${g.n}`}>
                <div className="eyebrow" style={{ color: "var(--ink)" }}>{g.n}</div>
                <h3 className="display-md" style={{ fontWeight: 700 }}>{g.t}</h3>
                <p className="body-md" style={{ maxWidth: 480, color: "var(--muted)" }}>{g.d}</p>
                <div style={{ textAlign: "right", color: "var(--green)" }}><ArrowUpRight size={28} strokeWidth={1.75} /></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FAQ ===================== */}
      <section className="section" data-testid="home-faq" style={{ borderTop: "1px solid var(--line)" }}>
        <div className="container-grav">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <div className="eyebrow" style={{ marginBottom: 20 }}><span className="sep" />FAQ</div>
              <h2 className="display-lg">
                Common questions <span className="text-green">from federal & enterprise buyers.</span>
              </h2>
              <p className="lede" style={{ marginTop: 24, color: "var(--muted)", maxWidth: 380 }}>
                Don't see your question?{" "}
                <Link to="/contact" className="arrow-link" style={{ display: "inline" }}>Ask the team</Link>
              </p>
            </div>

            <div className="lg:col-span-8">
              <div style={{ borderTop: "1px solid var(--ink)" }}>
                {[
                  {
                    q: "How long does a typical engagement take?",
                    a: "Discovery and architecture work spans 2–6 weeks. Full DevSecOps pipeline implementations run 3–6 months. Continuous ATO programs are ongoing with quarterly milestones. Every engagement is scoped to a fixed-fee or T&M ceiling — no surprises mid-flight."
                  },
                  {
                    q: "How do you accelerate ATO timelines?",
                    a: "Through automated evidence collection, STIG-hardened baselines, and continuous control monitoring, we routinely cut ATO from 6+ months to under 4 weeks. We deliver the SSP, POA&M, and ConMon artifacts the authorizing official needs — not just a slide deck."
                  },
                  {
                    q: "Are you vendor-neutral, or do you resell platforms?",
                    a: "We do not resell. Our recommendations are engineered to your security boundary, budget, and policy — not a partner quota. We hold partner-level certifications across AWS, Azure, GCP, HashiCorp, Red Hat, and others so we can deliver, but the procurement path is always yours."
                  },
                  {
                    q: "What contract vehicles do you operate under?",
                    a: "We're actively pursuing GSA MAS, SEWP, and CIO-SP3/OASIS+ vehicles. Today, we engage through direct contracts, teaming arrangements with prime contractors, and SBA 8(a) partnerships. Tell us your required vehicle and we'll scope the right path."
                  },
                  {
                    q: "Do you provide ongoing support after the initial engagement?",
                    a: "Yes. We offer managed DevSecOps, managed Kubernetes, and continuous compliance retainers — including 24/7 SOC integration, ConMon, and pipeline operations. Most clients move from project delivery into a managed-services relationship."
                  },
                  {
                    q: "Can you work in IL5, air-gapped, or classified environments?",
                    a: "Yes. We engineer for and operate in IL4–IL5 environments today, with experience designing air-gapped CI/CD architectures for IC and DoD mission systems. Cleared resources are available for engagements that require them."
                  }
                ].map((item, i) => <FAQItem key={i} index={i} {...item} />)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== CTA ===================== */}
      <section style={{ padding: "120px 0", background: "var(--green)", color: "#fff" }}>
        <div className="container-grav">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="eyebrow" style={{ color: "rgba(255,255,255,0.8)", marginBottom: 28 }}><span className="sep" style={{ background: "rgba(255,255,255,0.8)" }} />Ready when you are</div>
              <h2 className="display-lg" style={{ color: "#fff" }}>
                Brief our team.<br />One business day response.
              </h2>
            </div>
            <div className="lg:col-span-4" style={{ display: "flex", justifyContent: "flex-start" }}>
              <Link to="/contact" className="btn btn-light" data-testid="bottom-cta-contact" style={{ fontSize: 16 }}>
                Request consultation <span className="arrow"><ArrowUpRight size={14} strokeWidth={2.5} /></span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
